/**
 * Sidebar Section - V2
 * 
 * PVR form, favourite prompt, MLML / My Listing components.
 * Used by both mobile and desktop v2 layouts.
 */

import dynamic from 'next/dynamic';
import Cookies from 'js-cookie';
import { getTenantById } from '@tenants';
import { MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import { useMyListingAgents } from 'hooks/use-my-listing-agents';
import LoadingText from 'components/loading-text';

import type { AddressPageCommonData, AddressPageEventHandlers } from '../../../shared';
import { SoldListingExperimentCard } from '../../../components/sold-listing-experiment-card';

//#region Dynamic Imports
const ConnectionRequest = dynamic(() => import('components/connection-request'), {
  loading: () => <LoadingText />,
});

const FavouritePrompt = dynamic(() => import('../../../components/favourite-prompt'), {
  loading: () => <LoadingText />, ssr: false,
});

const MyListingAgentCard = dynamic(() => import('components/my-listing-agent-card'), {
  loading: () => <LoadingText />,
});

const MyLinkMyLeadCreate = dynamic(() => import('components/my-link-my-lead-create'), {
  loading: () => <LoadingText />,
});
//#endregion

export interface SidebarSectionProps {
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
  /**
   * Optional override for the connection request component.
   * When provided, replaces the default ConnectionRequest (PVR form).
   * The connectionRequestSlot is expected to handle its own agent card
   * rendering (e.g. ConnectionRequestSidebarSection integrates it internally).
   */
  connectionRequestSlot?: React.ReactNode;
}

/**
 * Sidebar Section
 * 
 * Renders the PVR form with the My Listing agent card integrated within it,
 * followed by favourite prompt.
 *
 * When `connectionRequestSlot` is provided (experiment ON), the slot is
 * responsible for rendering the agent card internally.
 *
 * When no slot is provided (experiment OFF), the agent card is rendered
 * above the default ConnectionRequest form within the same container.
 */
export function SidebarSection({ data, handlers, connectionRequestSlot }: SidebarSectionProps) {
  const {
    tenantId,
    listing,
    isSaved,
    soldListingExperimentId,
  } = data;

  const {
    onSeeAllSavedListingsClick,
    onSaveListingClick,
  } = handlers;

  const tenant = getTenantById(tenantId);
  const showsMLMLCreate = tenant.features.MyLinkMyLead && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const showAgentCard = tenant.features.MyLinkMyLead && !showsMLMLCreate;
  const { agents, shouldShow: hasAgents } = useMyListingAgents(listing);

  if (showsMLMLCreate) {
    return <MyLinkMyLeadCreate listing={listing} />;
  }

  return (
    <>
      {listing.isSold ? (
        soldListingExperimentId ? (
          <SoldListingExperimentCard mode={soldListingExperimentId} listing={listing} />
        ) : null
      ) : (
        <>
          {connectionRequestSlot ?? (
            <ConnectionRequest
              listing={listing}
              isAddressPage={true}
              agentCardSlot={showAgentCard && hasAgents ? <MyListingAgentCard agents={agents} layout="row" /> : undefined}
            />
          )}
          <FavouritePrompt
            listingId={listing.id}
            isFavourited={isSaved}
            onSeeAllSavedListingsClick={onSeeAllSavedListingsClick}
            onAddToFavouritesClick={onSaveListingClick}
          />
        </>
      )}
    </>
  );
}

export default SidebarSection;
